import { connect } from 'react-redux';
import { get, isEmpty } from 'lodash';
import i18next from 'i18next';
import React, { useState } from 'react';

import { Divider } from '@mui/material';

import { closeGenericModal, openGenericModal, refreshGenericModal } from '@actions/modal';
import { loading, loadingSuccess } from '@actions/loading';
import { showErrorMessage, showSuccessMessage } from '@actions/messageconfirmation';

import { Button } from '@commons/utils/styledLibraryComponents';
import { ExportButton } from '@commons/ExportButton';
import { IMPORT_MODAL_STEPS } from '@commons/Modals/MultiStepModal';

import { formatTranslationsUpdateFile } from '../formatTranslationsUpdateFile';
import { getImportErrorFileConfig } from '../importErrorFile';
import { getUpdateTranslationsModalConfig } from '../modalConfig';

import { downloadFile, formatXlsFileToJson } from '@backoffice/utils';

import { ButtonContainer } from '../styledComponents';

import translationsService from '@services/translations';

import TranslationsTable from './TranslationsTable';

export const EditTranslationsCardContent = (props) => {
  const {
    // modal,
    openGenericModal,
    refreshGenericModal,
    closeGenericModal,
    pageLoading,
    pageLoaded,
    showSuccessMessage,
    showErrorMessage,
  } = props;

  const [translationsProgress, setTranslationsProgress] = useState({});
  const [isLoadingProgress, setIsLoadingProgress] = useState(false);

  const openTranslationsUpdateImportModal = () => {
    const isModalOpen = get(props, 'modal.GenericModalBool', false);

    if (isModalOpen) {
      return;
    }

    const params = getUpdateTranslationsModalConfig(IMPORT_MODAL_STEPS.SELECT_FILE, {
      handleCloseCleanUp: cleanUpTranslationsImport,
      updatedFile: null,
      selectedFile: null,
      handleFileChange: onImportTranslationsFileChange,
      handleFileValidation: handleTranslationsUpdateFileValidation,
    });

    openGenericModal(params);
  };

  const onImportTranslationsFileChange = (file) => {
    refreshGenericModal(
      getUpdateTranslationsModalConfig(IMPORT_MODAL_STEPS.VALIDATE_FILE, {
        updatedFile: file,
        handleCloseCleanUp: cleanUpTranslationsImport,
        handleFileChange: onImportTranslationsFileChange,
        handleFileValidation: handleTranslationsUpdateFileValidation,
      }),
    );
  };

  const cleanUpTranslationsImport = () => {
    closeGenericModal();
  };

  const handleTranslationsUpdateErrorFile = (file) => {
    refreshGenericModal(
      getUpdateTranslationsModalConfig(IMPORT_MODAL_STEPS.ERROR_FILE, {
        updatedFile: file,
        handleFileChange: onImportTranslationsFileChange,
        handleCloseCleanUp: cleanUpTranslationsImport,
      }),
    );
  };

  const hasInvalidLinesOrSheet = ({
    unValidatedSupplierProductsSheetLanguageColumns,
    unValidatedIngredientsSheetLanguageColumns,
    unValidatedRecipesSheetLanguageColumns,
    unValidatedProductsSheetLanguageColumns,
    unValidatedCategoriesSheetLanguageColumns,
    unValidatedSubCategoriesSheetLanguageColumn,
    unValidatedSupplierProducts,
    unValidatedIngredients,
    unValidatedRecipes,
    unValidatedProducts,
    unValidatedCategories,
    unValidatedSubCategories,
  }) =>
    !isEmpty(unValidatedSupplierProductsSheetLanguageColumns) ||
    !isEmpty(unValidatedIngredientsSheetLanguageColumns) ||
    !isEmpty(unValidatedRecipesSheetLanguageColumns) ||
    !isEmpty(unValidatedProductsSheetLanguageColumns) ||
    !isEmpty(unValidatedCategoriesSheetLanguageColumns) ||
    !isEmpty(unValidatedSubCategoriesSheetLanguageColumn) ||
    !isEmpty(unValidatedSupplierProducts) ||
    !isEmpty(unValidatedIngredients) ||
    !isEmpty(unValidatedRecipes) ||
    !isEmpty(unValidatedProducts) ||
    !isEmpty(unValidatedCategories) ||
    !isEmpty(unValidatedSubCategories);

  const handleUnvalidatedFile = (
    file,
    {
      unValidatedSupplierProductsSheetLanguageColumns,
      unValidatedIngredientsSheetLanguageColumns,
      unValidatedRecipesSheetLanguageColumns,
      unValidatedProductsSheetLanguageColumns,
      unValidatedCategoriesSheetLanguageColumns,
      unValidatedSubCategoriesSheetLanguageColumn,
      unValidatedSupplierProducts,
      unValidatedIngredients,
      unValidatedRecipes,
      unValidatedProducts,
      unValidatedCategories,
      unValidatedSubCategories,
    },
  ) => {
    refreshGenericModal(
      getUpdateTranslationsModalConfig(IMPORT_MODAL_STEPS.ERROR_FILE, {
        updatedFile: file,
        handleFileChange: onImportTranslationsFileChange,
        handleCloseCleanUp: cleanUpTranslationsImport,
        errorFile: file,
        exportErrors: () =>
          getImportErrorFileConfig(
            unValidatedSupplierProducts,
            unValidatedIngredients,
            unValidatedRecipes,
            unValidatedProducts,
            unValidatedCategories,
            unValidatedSubCategories,
            unValidatedSupplierProductsSheetLanguageColumns,
            unValidatedIngredientsSheetLanguageColumns,
            unValidatedRecipesSheetLanguageColumns,
            unValidatedProductsSheetLanguageColumns,
            unValidatedCategoriesSheetLanguageColumns,
            unValidatedSubCategoriesSheetLanguageColumn,
          ),
      }),
    );
  };

  const handleTranslationsUpdateFileImport = async (formattedFileTranslationsData) => {
    pageLoading();

    try {
      await translationsService.importTranslations(formattedFileTranslationsData);

      cleanUpTranslationsImport();

      fetchTranslationsProgress();

      showSuccessMessage(i18next.t('ADMIN.TRANSLATIONS.IMPORT_MODAL_UPDATE_SUCCESS'));
    } catch {
      showErrorMessage(i18next.t('ADMIN.TRANSLATIONS.IMPORT_MODAL_UPDATE_ERROR'));
    } finally {
      pageLoaded();
    }
  };

  /** API CALLS */
  const getTranslationsUpdateTemplate = async () => {
    pageLoading();

    try {
      const template = await translationsService.getTranslationsTemplate();

      downloadFile(template, i18next.t('ADMIN.TRANSLATIONS.DOWNLOAD_TEMPLATE_FILENAME'));
    } catch {
      showErrorMessage(i18next.t('GENERAL.FILE_DOWNLOAD_ERROR'));
    } finally {
      pageLoaded();
    }
  };

  const handleTranslationsUpdateFileValidation = async (file) => {
    pageLoading();

    try {
      const fileTranslationsData = await formatXlsFileToJson(file);

      const formattedFileTranslationsData = formatTranslationsUpdateFile(fileTranslationsData);

      const validationResult = await translationsService.validateBatchUpdateImportTemplate(
        formattedFileTranslationsData,
      );

      if (hasInvalidLinesOrSheet(validationResult)) {
        handleUnvalidatedFile(file, validationResult);
        return;
      }

      refreshGenericModal(
        getUpdateTranslationsModalConfig(IMPORT_MODAL_STEPS.VALIDATED_FILE, {
          updatedFile: file,
          handleCloseCleanUp: cleanUpTranslationsImport,
          handleFileImport: () => handleTranslationsUpdateFileImport(formattedFileTranslationsData),
        }),
      );
    } catch (error) {
      handleTranslationsUpdateErrorFile(file);
    } finally {
      pageLoaded();
    }
  };

  const fetchTranslationsProgress = async () => {
    setIsLoadingProgress(true);
    try {
      const data = await translationsService.getTranslationsProgress();

      setTranslationsProgress(data);
    } catch {
      showErrorMessage(i18next.t('ADMIN.TRANSLATIONS.FETCH_PROGRESS_ERROR'));
      setTranslationsProgress({});
    } finally {
      setIsLoadingProgress(false);
    }
  };

  return (
    <>
      <ButtonContainer>
        <ExportButton
          customLabel={i18next.t('ADMIN.TRANSLATIONS.DOWNLOAD_TRANSLATIONS')}
          customStyle={{ padding: '0px', justifyContent: 'left' }}
          handleClick={getTranslationsUpdateTemplate}
          noBorder
        />
        <Button
          buttonCustomStyle={{ padding: '0px', justifyContent: 'left' }}
          color={'inpulse-outline'}
          fontSize={14}
          formatText={false}
          handleClick={openTranslationsUpdateImportModal}
          icon={'/images/inpulse/file-upload-black-small.svg'}
          iconCustomStyle={{ width: '18px', height: '18px' }}
          label={i18next.t('ADMIN.TRANSLATIONS.UPLOAD_TRANSLATIONS')}
          noBorder
        />
      </ButtonContainer>
      <Divider />
      <TranslationsTable
        fetchTranslationsProgress={fetchTranslationsProgress}
        isLoadingProgress={isLoadingProgress}
        translationsProgress={translationsProgress}
      />
    </>
  );
};

const mapStateToProps = (state) => ({
  modal: state.modalReducer,
});

const mapDispatchToProps = (dispatch) => ({
  showSuccessMessage: (message) => {
    dispatch(showSuccessMessage(message));
  },
  showErrorMessage: (message) => {
    dispatch(showErrorMessage(message));
  },
  openGenericModal: (params) => dispatch(openGenericModal(params)),
  refreshGenericModal: (params, customPropertyPathsToUpdate) => {
    dispatch(refreshGenericModal(params, customPropertyPathsToUpdate));
  },
  closeGenericModal: () => {
    dispatch(closeGenericModal());
  },
  pageLoading: () => {
    dispatch(loading());
  },
  pageLoaded: () => {
    dispatch(loadingSuccess());
  },
});

export default connect(mapStateToProps, mapDispatchToProps)(EditTranslationsCardContent);
